/**
 * bodyOverlays — replace-semantics registry for UI singletons attached to
 * `document.body`. Auto-advance overlay, photo-source picker, share-card
 * preview modal — each has at most one active instance at a time; a second
 * `register(kind, ...)` call tears down the previous one before the new one
 * becomes active.
 *
 * Hidden state was the old shape: each module had its own `let activeXxx`
 * module-level mutable, and a forgotten `clearAutoAdvanceOverlay()` in one
 * view's destroy left a stacked overlay over the next mount. Replace-semantics
 * lives inside this registry now — the call sites just `register(...)` and
 * forget. Same lifecycle-coordinator pattern as ViewLifecycle: foot-gun lives
 * inside the primitive.
 *
 * `lc` is passed as an explicit parameter, not inferred from any "current"
 * scope. Honesty over hidden state — view-scoped overlays are always tied to
 * their owner view's lifecycle, and switching scope (e.g. moving the share-
 * card to a different view) becomes a visible signature change.
 *
 * Out-of-scope (does NOT use bodyOverlays):
 * - `components/connection-status.ts` — app-global banner survives view swaps
 *   by design (`/review 2026-05-04 v0.80.7 F1`).
 * - `components/emoji-picker.ts` — appends to view container, not body.
 * - `components/team-event-toast.ts` — STACKING semantics required, NOT
 *   replace-semantics. Toasts coexist (PENALTY + GEO_VERIFIED in the same
 *   WS tick is a deliberate design point); `bodyOverlays.register` would
 *   fire the SHARED `dismissAllTeamToasts` sweep on the second call from
 *   the same view and wipe a freshly-rendered toast. View-scoped lifetime
 *   is wired per-toast via `lc.onDestroy(dismiss)` — see the JSDoc in
 *   `team-event-toast.ts::showToastWithLc` for the full rationale.
 */

export type OverlayKind =
  | 'auto-advance'
  | 'photo-source'
  | 'share-card';

const active: Map<OverlayKind, () => void> = new Map();

/**
 * Register a body-attached overlay's cleanup. If another instance of the same
 * `kind` is already active, its cleanup runs first (replace-semantics).
 *
 * The cleanup is also added to `lc.onDestroy(...)` — when the owner view tears
 * down, the overlay tears down with it. View-scoped lifetime is the default.
 *
 * Cleanup runs exactly once even if both eviction-by-replace AND lc.cleanup
 * fire — the second call is a no-op because the registry entry was already
 * cleared.
 */
export function register(kind: OverlayKind, cleanup: () => void, lc: ViewLifecycle): void {
  // Replace-semantics: evict any existing instance of this kind.
  const prev = active.get(kind);
  if (prev) {
    active.delete(kind);
    try { prev(); } catch (e) {
      console.error(`[bodyOverlays] replace-evict cleanup for kind=${kind} threw:`, e);
    }
  }

  // Wrap so that BOTH eviction-by-replace AND lc.cleanup are idempotent — the
  // cleanup runs exactly once regardless of which side fires first.
  let fired = false;
  const wrapped = (): void => {
    if (fired) return;
    fired = true;
    active.delete(kind);
    try { cleanup(); } catch (e) {
      console.error(`[bodyOverlays] cleanup for kind=${kind} threw:`, e);
    }
  };

  active.set(kind, wrapped);
  lc.onDestroy(wrapped);
}

// Import type without value to avoid a circular dependency at runtime.
import type { ViewLifecycle } from './view-lifecycle';
